<template>
  <b-overlay :show="loading" opacity="0.8">
    <b-card class="rounded-lg">
      <div class="d-flex align-items-center justify-content-end">
        <div v-if="patientData">
          <PatientOverviewActionCard 
            :patient-data="patientData" 
            :consultation-data="consultationData"
            @refresh="$emit('refresh')"
          />
        </div>
      </div>

      <div class="card-body">
        <b-row>
          <b-col cols="12" md="7" class="pr-2">
            <div class="text-center">
              <b-avatar 
                ref="previewEl" 
                size="10rem" 
                variant="light-success" 
                :src="getValueFromSource(patientData, 'avatar.path')"
                :text="avatarText(`${getValueFromSource(patientData, 'full_name')}`)" 
              />

              <h2 class="mt-2 text-dark">
                {{ getValueFromSource(patientData, 'full_name') }}
              </h2>
            </div>

            <div class="d-flex justify-content-between align-items-center mt-5">
              <div class="w-50">
                <h5 class="text-muted">
                  Gender
                </h5>

                <h5 class="lead text-dark">
                  {{ startCase(getValueFromSource(patientData, 'gender')) }}
                </h5>
              </div>

              <div class="w-50">
                <h5 class="text-muted text-center">
                  BMR 
                </h5>

                <h5 class="lead text-dark text-right">
                  {{ calculatedBMR }}<sub>kcal/day</sub>
                </h5>
              </div>

              <div class="w-50">
                <h5 class="text-muted text-right">
                  BMI <span :style="{ color: getBMIColor }">({{ getBMIState }})</span>
                </h5>

                <h5 class="lead text-dark text-right" :style="{ color: getBMIColor }">
                  {{ calculatedBMI }}<sub>kg/m2</sub>
                </h5>
              </div>
            </div>

            <div class="d-flex justify-content-between align-items-center mt-2">
              <div class="w-50">
                <h5 class="text-muted">
                  DOB
                </h5>

                <h5 class="lead text-dark">
                  {{ getValueFromSource(patientData, 'date_of_birth') }}
                </h5>
              </div>

              <div class="w-50">
                <h5 class="text-muted text-right">
                  Age
                </h5>

                <h5 class="lead text-dark text-right">
                  {{ calculateAge( new Date(getValueFromSource(patientData, 'date_of_birth'))) }}<sub>y</sub>
                </h5>
              </div>
            </div>

            <div class="d-flex justify-content-between align-items-center mt-2">
              <div class="w-50">
                <h5 class="text-muted">
                  Weight
                </h5>

                <h5 class="lead text-dark">
                  {{ getValueFromSource(consultationData, 'anthropometric_data.weight') }}<sub>kg</sub>
                </h5>
              </div>

              <div class="w-50">
                <h5 class="text-muted text-right">
                  Height
                </h5>

                <h5 class="lead text-dark text-right">
                  {{ getValueFromSource(consultationData, 'anthropometric_data.height') }}<sub>cm</sub>
                </h5>
              </div>
            </div>

            <div class="d-flex justify-content-between align-items-center">
              <b-button
                size="lg"
                pill
                variant="primary"
                class="mt-2 mr-2"
              >
                <feather-icon
                  icon="MailIcon"
                  size="20"
                />
                <span class="ml-25 align-middle">Message</span>
              </b-button>
      
              <b-button
                pill
                size="lg"
                class="mt-2"
                variant="primary"
                :disabled="lockCallButton"
                @click="onStartCall"
              >
                <feather-icon
                  icon="VideoIcon"
                  size="22"
                />
                <span class="ml-25 align-middle">Start Call</span>
              </b-button>
            </div>

          </b-col>

          <b-col cols="12" md="5" style="border-left: 2px solid rgb(238, 238, 238);" class="pl-1 p-0">
            <div class="mb-3">
              <h5 class="lead text-muted">
                Home Address
              </h5>

              <h4 class="text-dark">
                <h5 class="lead text-dark">
                  {{ getValueFromSource(patientData, 'home_address', 'N/A') || 'N/A' }}
                </h5>
              </h4>
            </div>

            <div class="mb-3">
              <h5 class="text-muted">
                Mobile Phone #
              </h5>

              <h4 class="lead text-dark">
                <b-link v-if="patientData.phone" :href="`tel:${patientData.phone}`">
                  {{ getValueFromSource(patientData, 'phone') }}
                </b-link>

                <span v-else>
                  N/A
                </span>
              </h4>
            </div>

            <div class="mb-3">
              <h5 class="text-muted">
                Work Phone #
              </h5>

              <h4 class="lead text-dark">
                <b-link v-if="patientData.work_phone" :href="`tel:${patientData.work_phone}`">
                  {{ getValueFromSource(patientData, 'work_phone') }}
                </b-link>

                <span v-else>
                  N/A
                </span>
              </h4>
            </div>

            <div class="mb-3">
              <h5 class="text-muted">
                Email
              </h5>

              <h4 class="lead text-dark">
                <b-link v-if="patientData.email" :href="`mailto:${patientData.email}`">
                  {{ getValueFromSource(patientData, 'email') }}
                </b-link>

                <span v-else>
                  N/A
                </span>
              </h4>
            </div>

            <div class="mb-3">
              <h5 class="text-muted">
                Insurance Name
              </h5>

              <h4 class="lead text-dark">
                {{ getValueFromSource(patientData, 'insurance_name', 'N/A') || 'N/A' }}
              </h4>
            </div>

            <div class="mb-3">
              <h5 class="text-muted">
                Policy Number
              </h5>

              <h4 class="lead text-dark">
                {{ getValueFromSource(patientData, 'policy_number', 'N/A') || 'N/A' }}
              </h4>
            </div>

            <div class="">
              <h5 class="text-muted">
                Consultation Date
              </h5>

              <h4 class="lead text-dark">
                {{ formatDate(getValueFromSource(consultationData, 'created'), 'yyyy-MM-dd') }}
              </h4>
            </div>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </b-overlay>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BLink,
  BBadge,
  BAlert,
  BButton,
  BAvatar,
  BOverlay,
  BDropdown,
  BDropdownItem,
  BDropdownGroup
} from 'bootstrap-vue'

import { startCase } from 'lodash';
import webRTCBus from '@core/mixins/bus/webrtc';
import WEB_RTC_ACTIONS from '@core/utils/webrtc';
import PatientOverviewActionCard from "@core/components/shared/admin/consultation/PatientOverviewActionCard.vue";

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BLink,
    BBadge,
    BAlert,
    BButton,
    BAvatar,
    BOverlay,
    BDropdown,
    BDropdownItem,
    BDropdownGroup,
    PatientOverviewActionCard,
  },
  props: {
    patientData: {
      type: Object,
      required: true,
    },
    consultationData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      lockCallButton: false
    }
  },
  computed: {
    calculatedBMR() {
      if (!this.patientData) return

      const age = this.calculateAge(new Date(this.patientData.date_of_birth))
      const {
        weight = 0,
        height = 0
      } = this.getValueFromSource(this.consultationData, 'anthropometric_data', {});

      if (this.patientData.gender === 'male'){
        const result = 88.362 + (13.397 * weight) + (4.799 * height) - (5.677 * age)
        return result.toFixed(2)
      }

      if (this.patientData.gender === 'female'){
        const result = 447.593 + (9.247 * weight) + (3.098 * height) - (4.330 * age)
        return result.toFixed(2)
      }

      return 0
    },
    getBMIColor() {
      if (this.calculatedBMI < 18.5){
        return 'lightblue !important'
      }

      if (this.calculatedBMI > 18.5 && this.calculatedBMI <= 24.9){
        return 'green !important'
      }

      if (this.calculatedBMI > 24.9 && this.calculatedBMI <= 29.9){
        return 'orange !important'
      }

      return 'red !important'
    },
    getBMIState() {
      if (this.calculatedBMI < 18.5){
        return 'Underweight'
      }

      if (this.calculatedBMI > 18.5 && this.calculatedBMI <= 24.9){
        return 'Healthy'
      }

      if (this.calculatedBMI > 24.9 && this.calculatedBMI <= 29.9){
        return 'Overweight'
      }

      return 'Obese'
    },
    calculatedBMI() {
      const {
        weight = 0,
        height = 0
      } = this.getValueFromSource(this.consultationData, 'anthropometric_data', {});

      if (!weight || !height){
        return 0
      }
      
      const heightInMeters = height / 100;
    
      // Calculate BMI using the formula: weight / (height * height)
      const bmi = weight / (heightInMeters * heightInMeters);
      
      // Round BMI to two decimal places
      return Math.round(bmi * 100) / 100;
    },
  },
  created() {
    webRTCBus.$on(WEB_RTC_ACTIONS.LOCK_CALL_BUTTON, (status) => {
      this.lockCallButton = status;
    })
  },
  methods: {
    startCase,
    onStartCall() {
      webRTCBus.$emit(WEB_RTC_ACTIONS.INITIATE_CALL, {
        patient: this.patientData,
        consultation: this.consultationData
      })
    }
  }
}
</script>

